import React from 'react';
import { useTranslation } from 'react-i18next';

// components
import Chatbar from 'eCoach/chatbar/Chatbar';
import ChatBubble from 'eCoach/ChatBubble';
import PromptSetup from 'eCoach/promptHelper/PromptSetup';
import QuestionGoal from 'eCoach/promptHelper/QuestionGoal';
import QuestionNoTheme from 'eCoach/promptHelper/QuestionNoTheme';
import QuestionTheme from 'eCoach/promptHelper/QuestionTheme';

// hooks and helpers
import useAutoScroll from 'eCoach/hooks/useAutoScroll';
import { PromptOption } from 'eCoach/promptHelper/prompts';

interface ChatGuideData {
  promptHelperGoal: PromptOption | string | null;
  promptHelperTheme: string | null;
  chatStartEnded: boolean;
}

interface PromptHelperProps {
  chatGuideData: ChatGuideData;
  handleChatGuidance: (data: Partial<ChatGuideData>) => void;
  onSubmit: (message: string) => void;
  startConvoPrep: () => void;
}

export default function PromptHelper({
  chatGuideData,
  handleChatGuidance,
  onSubmit,
  startConvoPrep,
}: PromptHelperProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const messagesEndRef = useAutoScroll(chatGuideData);

  const handleSelectedGoal = (selection: PromptOption | string) => {
    if (
      selection === 'Preparing for a difficult conversation' ||
      selection === 'Something else'
    ) {
      startConvoPrep();
    } else {
      handleChatGuidance({ promptHelperGoal: selection });
    }
  };

  const handleSelectedTheme = (selection: string) =>
    handleChatGuidance({ promptHelperTheme: selection });

  const selectedGoal = chatGuideData.promptHelperGoal;
  const selectedTheme = chatGuideData.promptHelperTheme;

  return (
    <>
      <ChatBubble
        simpleString={t(
          "promptHelper.I need advice on a challenge I'm currently facing."
        )}
        sentByUser
      />
      <QuestionTheme
        handleSelectedTheme={handleSelectedTheme}
        selectedTheme={selectedTheme}
      />
      {selectedTheme && selectedTheme === 'Something else' && (
        <QuestionNoTheme />
      )}
      {selectedTheme && selectedTheme !== 'Something else' && (
        <QuestionGoal
          handleSelectedGoal={handleSelectedGoal}
          selectedGoal={selectedGoal}
          selectedTheme={selectedTheme}
        />
      )}
      {selectedTheme && selectedTheme !== 'Something else' && selectedGoal ? (
        <PromptSetup chatGuideData={chatGuideData} onSubmit={onSubmit} />
      ) : (
        <Chatbar onSubmit={onSubmit} />
      )}
      <div ref={messagesEndRef} style={{ margin: 0 }} />
    </>
  );
}
